import type { Entity } from '@backstage/catalog-model';
import { PREREQUISITE_DEFINITIONS, type PrerequisiteDefinition } from './prerequisiteDefinitions';

/**
 * Annotation listing the prerequisites a user must satisfy before using a
 * template (e.g. membership of the Equinor GitHub organization).
 *
 * Value is a comma-separated list of prerequisite keys (see
 * {@link PREREQUISITE_DEFINITIONS}). Whitespace around commas is ignored, but a
 * comma is required to separate keys. The display text and url live in the
 * registry — keeping templates free of long multi-line copy and letting a
 * single edit update every template. Unknown keys are ignored.
 *
 * @example
 * ```yaml
 * metadata:
 *   annotations:
 *     equinor.com/template-prerequisites: github, radix
 * ```
 */
export const TEMPLATE_PREREQUISITES_ANNOTATION = 'equinor.com/template-prerequisites';

export interface Prerequisite extends PrerequisiteDefinition {
  key: string;
}

const parseKeys = (raw: string): string[] =>
  raw
    .split(',')
    .map(key => key.trim())
    .filter(Boolean);

/**
 * Whether the template declares the prerequisites annotation at all.
 *
 * Distinguishes "no requirements defined" (annotation missing) from "no
 * requirements" (annotation present but empty).
 */
export const hasPrerequisitesAnnotation = (entity: Entity): boolean =>
  entity.metadata.annotations?.[TEMPLATE_PREREQUISITES_ANNOTATION] !== undefined;

export const parsePrerequisites = (entity: Entity): Prerequisite[] => {
  const raw = entity.metadata.annotations?.[TEMPLATE_PREREQUISITES_ANNOTATION];
  if (!raw) {
    return [];
  }
  return parseKeys(raw)
    .map(key => {
      const definition = PREREQUISITE_DEFINITIONS[key];
      return definition ? { key, ...definition } : undefined;
    })
    .filter((prerequisite): prerequisite is Prerequisite => prerequisite !== undefined);
};

export const prerequisiteId = (prerequisite: Prerequisite): string => prerequisite.key;
